import { useState, useEffect, useRef } from "react";
import { loginUser } from "../services/authService";
import { useAuth } from "../context/AuthContext";
import { useNavigate, Link } from "react-router-dom";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { ChevronLeft, ArrowUpRight } from "lucide-react";
import { Eye, EyeOff } from "lucide-react";

export default function LoginPageCrew() {
  const { login } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [passwordVisible, setPasswordVisible] = useState(false);

  // VIDEO SEQUENCE LOGIC
  const videos = [
    "/images/login1.mp4",
    "/images/login3.mp4",
    "/images/login2.mp4",
  ];

  const [currentVideo, setCurrentVideo] = useState(0);

  // SWIPE/DRAG LOGIC REFS
  const dragStartX = useRef(null);
  const minSwipeDistance = 50; // Minimum pixels to drag to trigger a change

  const handleNextVideo = () => {
    setCurrentVideo((prev) => (prev + 1) % videos.length);
  };

  const handlePrevVideo = () => {
    setCurrentVideo((prev) => (prev - 1 + videos.length) % videos.length);
  };

  // Automatic transition logic
  useEffect(() => {
    const interval = setInterval(() => {
      handleNextVideo();
    }, 4000);

    return () => clearInterval(interval);
  }, [currentVideo]); // Reset interval whenever currentVideo changes (manual or auto)

  // Mouse/Touch Handlers
  const handleDragStart = (e) => {
    // Check if it's a touch event or mouse event
    dragStartX.current = e.clientX || (e.touches && e.touches[0].clientX);
  };

  const handleDragEnd = (e) => {
    if (dragStartX.current === null) return;

    const dragEndX = e.clientX || (e.changedTouches && e.changedTouches[0].clientX);
    const distance = dragStartX.current - dragEndX;

    // If moved left (distance positive) -> Next
    if (distance > minSwipeDistance) {
      handleNextVideo();
    }
    // If moved right (distance negative) -> Previous
    else if (distance < -minSwipeDistance) {
      handlePrevVideo();
    }

    dragStartX.current = null;
  };

  const handleLogin = async () => {
    try {
      setError("");

      const data = await loginUser({ ...form });

      login(data.token, data.user);

      if (data.user?.userTypeId === 1) {
        navigate("/dashboard");
      } else if (data.user?.userTypeId === 2) {
        navigate("/creator-dashboard");
      } else {
        navigate("/");
      }
    } catch (err) {
      setError("Invalid email or password");
    }
  };

  return (
    <div className="w-full h-screen flex overflow-hidden">
      {/* LEFT FORM SECTION */}
      <div className="w-full lg:w-1/2 bg-[#fdf9f2] flex flex-col min-h-screen px-4 sm:px-10 lg:px-14 py-14 overflow-y-auto no-scrollbar">
        {/* TOP: BACK + LOGO ALIGNED WITH FORM WIDTH */}
        <div className="w-full flex justify-center">
          <div className="max-w-md w-full flex items-center justify-between mb-8">
            {/* Back Button */}
            <button
              onClick={() => navigate("/")}
              className="flex items-center text-gray-600 text-sm hover:text-black"
            >
              <ChevronLeft size={18} className="mr-1" />
              Back
            </button>
            {/* Logo */}
            <img
              src="/images/Container.png"
              alt="Beige Logo"
              className="w-24 object-contain"
            />
          </div>
        </div>

        {/* CENTER FORM CONTENT */}
        <div className="flex flex-col justify-start lg:justify-center flex-grow max-w-md w-full mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">
            Welcome Back
          </h2>

          <p className="text-gray-500 mb-8 text-center">
            Log in to continue to your creative dashboard.
          </p>

          <div className="space-y-5">
            {/* Email */}
            <div>
              <label className="text-sm font-medium mb-1 block">
                Email Address
              </label>
              <Input
                type="email"
                placeholder="you@example.com"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-medium">Password</label>
                <Link
                  to="/forgot-password"
                  className="text-xs text-gray-600 hover:underline"
                >
                  Forgot your password?
                </Link>
              </div>

              <div className="relative">
                <Input
                  type={passwordVisible ? "text" : "password"}
                  placeholder="Enter your password"
                  value={form.password}
                  onChange={(e) =>
                    setForm({ ...form, password: e.target.value })
                  }
                />
                <button
                  type="button"
                  onClick={() => setPasswordVisible(!passwordVisible)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-600"
                >
                  {passwordVisible ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            {error && <p className="text-red-500 text-sm">{error}</p>}

            {/* Remember Me */}
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                className="
                  w-4 h-4 border rounded appearance-none
                  checked:bg-black checked:border-black
                  checked:before:content-['✓']
                  checked:before:text-white
                  checked:before:flex checked:before:items-center checked:before:justify-center
                  before:text-xs before:font-bold
                "
              />
              Remember me
            </label>

            {/* Sign In Button */}
            <Button
              className="w-full bg-black text-white py-3"
              onClick={handleLogin}
            >
              Sign In
            </Button>
          </div>

          {/* SIGN UP TEXT WITH LINES */}
          <div className="relative flex items-center justify-center mt-12 mb-6">
            <div className="flex-grow h-px bg-[#E5E5E5]"></div>
            <span className="px-2 text-[#737373]">Don't have an account?</span>
            <div className="flex-grow h-px bg-[#E5E5E5]"></div>
          </div>

          {/* SIGN UP CARDS */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {/* Creator Card */}
            <div
              onClick={() => navigate("/creative-profile/signup")}
              className="cursor-pointer bg-[#F9F4EB] border-[1px] border-[#F5EBDA] rounded-xl p-4 flex flex-col justify-between h-[140px] sm:h-[118px] transition-all duration-300 ease-in-out hover:bg-white hover:border-[#F4DAB1] hover:scale-105 hover:shadow-lg"
            >
              <div className="flex items-center justify-between h-full">
                <div className="flex flex-col justify-between w-3/5">
                  <p className="font-medium mb-2 text-sm sm:text-base">
                    Sign Up as a Creator
                  </p>
                  <button className="w-8 h-8 rounded-full border bg-white flex items-center justify-center">
                    <ArrowUpRight size={16} />
                  </button>
                </div>
                <div className="w-2/5 relative">
                  <img
                    src="/images/signup-creator.png"
                    alt="Creator"
                    className="w-full h-auto object-cover mt-2 transition-all duration-300 ease-in-out hover:scale-110"
                  />
                </div>
              </div>
            </div>

            {/* Admin Card */}
            <div
              onClick={() => navigate("/signup-admin")}
              className="cursor-pointer bg-[#F9F4EB] border-[1px] border-[#F5EBDA] rounded-xl p-4 flex flex-col justify-between h-[140px] sm:h-[118px] transition-all duration-300 ease-in-out hover:bg-white hover:border-[#F4DAB1] hover:scale-105 hover:shadow-lg"
            >
              <div className="flex items-center justify-between h-full">
                <div className="flex flex-col justify-between w-3/5">
                  <p className="font-medium mb-2 text-sm sm:text-base">
                    Sign Up As An Admin
                  </p>
                  <button className="w-8 h-8 rounded-full border bg-white flex items-center justify-center">
                    <ArrowUpRight size={16} />
                  </button>
                </div>
                <div className="w-2/5 relative">
                  <img
                    src="/images/signup-admin.png"
                    alt="Admin"
                    className="w-full h-auto object-cover mt-4 transition-all duration-300 ease-in-out hover:scale-110"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* RIGHT VIDEO SECTION */}
      <div
        className="hidden lg:flex w-1/2 relative overflow-hidden cursor-grab active:cursor-grabbing"
        onMouseDown={handleDragStart}
        onMouseUp={handleDragEnd}
        onTouchStart={handleDragStart}
        onTouchEnd={handleDragEnd}
      >
        {videos.map((src, index) => (
          <video
            key={index}
            src={src}
            muted
            autoPlay
            loop
            playsInline
            preload="auto"
            className={`
        absolute inset-0 w-full h-full object-cover pointer-events-none
        transition-all duration-[2500ms] ease-[cubic-bezier(0.4,0,0.2,1)]
        will-change-[opacity,transform]
        ${
          currentVideo === index
            ? "opacity-100 scale-100 z-10"
            : "opacity-0 scale-105 z-0"
        }
      `}
          />
        ))}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent z-20 pointer-events-none"></div>

        {/* Bottom Caption */}
        <div className="absolute bottom-10 left-0 w-full text-center text-white z-30 px-10">
          <div className="pointer-events-none">
          {currentVideo === 0 && (
            <>
              <h3 className="text-2xl font-bold">Editor</h3>
              <p className="text-sm opacity-90 mt-2">
                Bring your creative vision to life with intuitive editing tools.
                Share your stories with the world!
              </p>
            </>
          )}

          {currentVideo === 1 && (
            <>
              <h3 className="text-2xl font-bold">Photography</h3>
              <p className="text-sm opacity-90 mt-2">
                Join a lively network of skilled photographers and discover
                amazing shooting opportunities!
              </p>
            </>
          )}

          {currentVideo === 2 && (
            <>
              <h3 className="text-2xl font-bold">Videography</h3>
              <p className="text-sm opacity-90 mt-2">
                Connect with a vibrant community of talented videographers and
                unlock fantastic shooting opportunities!
              </p>
            </>
          )}
          </div>

          {/* Dots */}
          <div className="flex justify-center gap-2 mt-3">
            {[0, 1, 2].map((i) => (
              <button
                key={i}
                onClick={() => setCurrentVideo(i)}
                className={`w-2 h-2 rounded-full transition-all duration-500 ${
                  currentVideo === i ? "bg-white scale-125" : "bg-white/50"
                }`}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
